import { useEffect, useState } from 'react';
import { Minus, Play, Plus } from 'lucide-react';
import { api } from '../api/client.js';
import ConfirmModal from './ConfirmModal.jsx';

// The match control: one set on start.gg, run with the user's own token
// through /api/packages/[slug]/match-control (users.start_match_enabled).
// Call and Start match; the games score (+ and − per player, reported as
// the games so far with no winner, which is the live score the overlays
// read); the winner, a DQ and a reset, each behind a confirmation. The
// In-Match card wraps it round the set on the overlay, the bracket page
// round any set. It re-reads the set every 20 s (start.gg's own site may be
// in use too) and takes every action's answer as the set's truth; onMatch
// hands that to the parent.
const MATCH_POLL_MS = 20_000;

// start.gg set states: 2 in progress, 3 completed, 6 called.
const IN_PROGRESS = 2;
const COMPLETED = 3;
const CALLED = 6;

export default function MatchControl({ packageSlug, setId, enabled, characterNames, onMatch }) {
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // The action waiting for a confirmation: { kind: 'winner' | 'dq' | 'reset', who }.
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    setMatch(null);
    setMessage(null);
    if (!setId) return undefined;
    let cancelled = false;
    const read = () => {
      if (document.visibilityState === 'hidden') return;
      api.packageMatch(packageSlug, setId)
        .then((r) => {
          if (cancelled || !r?.match) return;
          setMatch(r.match);
          onMatch?.(r.match);
        })
        .catch((e) => { if (!cancelled) setMessage(e.message); });
    };
    read();
    const timer = setInterval(read, MATCH_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageSlug, setId]);

  const act = async (body) => {
    setBusy(true);
    setMessage(null);
    try {
      const r = await api.packageMatchControl(packageSlug, { setId, ...body });
      if (r?.match) {
        setMatch(r.match);
        onMatch?.(r.match);
      }
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  // The games as start.gg has them, who won each; + and − send the whole
  // list back, so the server's answer is the truth the strip shows.
  const winners = (match?.games ?? []).map((g) => g.winner).filter((w) => w === 1 || w === 2);
  const score = (side) => winners.filter((w) => w === side).length;
  const firstTo = match?.bestOf ? Math.ceil(match.bestOf / 2) : null;
  const playersKnown = !!match && match.players.length === 2 && match.players.every((p) => p.id);
  const on = enabled && !busy && !!match;
  const canReport = on && playersKnown && !match.completed;
  const started = match?.state === IN_PROGRESS;
  const over = !!match?.completed || match?.state === COMPLETED;
  const name = (side) => match?.players?.[side - 1]?.name ?? 'TBD';
  const character = (side) => characterNames?.[side - 1] ?? null;

  const addGame = (side) => act({ action: 'games', winners: [...winners, side] });
  const removeGame = (side) => {
    const i = winners.lastIndexOf(side);
    if (i >= 0) act({ action: 'games', winners: winners.filter((_, k) => k !== i) });
  };
  const confirmAction = () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.kind === 'winner') act({ action: 'winner', winner: c.who, winners });
    else if (c.kind === 'dq') act({ action: 'dq', dq: c.who });
    else act({ action: 'reset' });
  };

  const scoreSide = (side) => (
    <div className="d-flex align-items-center gap-2">
      <button
        type="button"
        className="btn-icon"
        aria-label={`Take a game from ${name(side)}`}
        disabled={!canReport || score(side) === 0}
        onClick={() => removeGame(side)}
      >
        <Minus size={14} aria-hidden="true" />
      </button>
      <span className="fs-4 fw-bold" aria-label={`${name(side)}: ${score(side)} games`}>{score(side)}</span>
      <button
        type="button"
        className="btn-icon"
        aria-label={`Give a game to ${name(side)}`}
        disabled={!canReport}
        onClick={() => addGame(side)}
      >
        <Plus size={14} aria-hidden="true" />
      </button>
    </div>
  );

  return (
    <div className="match-control">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="btn-ghost btn-ghost--primary"
          disabled={!on || started || over || match?.state === CALLED}
          onClick={() => act({ action: 'call' })}
          title="Marks this match called on start.gg: the players are wanted at the setup"
        >
          Call
        </button>
        <button
          type="button"
          className="btn-pill btn-pill--primary"
          disabled={!on || started || over}
          onClick={() => act({ action: 'start' })}
          title="Marks this match in progress on start.gg, which is when the In-Match overlay shows it"
        >
          <Play size={14} aria-hidden="true" /> {busy ? 'Working…' : 'Start match'}
        </button>
        {!enabled && (
          <span className="text-muted small">No start.gg token for this account yet: the admin adds one.</span>
        )}
      </div>

      {match && (
        <div className="mb-2">
          <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <div>
              <strong>{name(1)}</strong>
              {character(1) && <div className="text-muted small">as {character(1)}</div>}
            </div>
            {scoreSide(1)}
            <span className="text-muted small">
              {match.bestOf ? `Best of ${match.bestOf}` : 'Games'}
              {match.winner ? ` · ${name(match.winner)} won` : ''}
            </span>
            {scoreSide(2)}
            <div className="text-end">
              <strong>{name(2)}</strong>
              {character(2) && <div className="text-muted small">as {character(2)}</div>}
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap mt-2">
            {[1, 2].map((side) => (
              <button
                key={side}
                type="button"
                className={firstTo && score(side) >= firstTo ? 'btn-pill btn-pill--primary' : 'btn-ghost btn-ghost--primary'}
                disabled={!canReport}
                onClick={() => setConfirm({ kind: 'winner', who: side })}
              >
                {name(side)} wins
              </button>
            ))}
            {[1, 2].map((side) => (
              <button
                key={side}
                type="button"
                className="btn-ghost btn-ghost--danger"
                disabled={!canReport}
                onClick={() => setConfirm({ kind: 'dq', who: side })}
              >
                DQ {name(side)}
              </button>
            ))}
            <button
              type="button"
              className="btn-ghost btn-ghost--danger"
              disabled={!on}
              onClick={() => setConfirm({ kind: 'reset' })}
            >
              Reset set
            </button>
          </div>
          <p className="text-muted small mt-2 mb-0">
            Each + or − is reported to start.gg at once, so the overlay&apos;s score
            follows. Reporting the winner ends the match on start.gg.
          </p>
        </div>
      )}

      {message && <p className="small text-danger mt-2 mb-0">{message}</p>}

      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.kind === 'winner' ? `${name(confirm.who)} wins?`
            : confirm?.kind === 'dq' ? `Disqualify ${name(confirm.who)}?`
              : 'Reset this set?'
        }
        body={
          confirm?.kind === 'winner'
            ? `Reports the set to start.gg as ${name(1)} ${score(1)}–${score(2)} ${name(2)}, with ${name(confirm.who)} as the winner. The bracket moves on.`
            : confirm?.kind === 'dq'
              ? `Reports the set to start.gg as a DQ of ${name(confirm.who)}; ${name(confirm.who === 1 ? 2 : 1)} advances.`
              : 'Puts the set back to not started on start.gg, with no games and no winner. Sets that already depend on its result are left alone, and start.gg refuses the reset while they stand.'
        }
        confirmLabel={confirm?.kind === 'winner' ? 'Report winner' : confirm?.kind === 'dq' ? 'Report DQ' : 'Reset'}
        confirmKind={confirm?.kind === 'winner' ? 'primary' : 'danger'}
        onConfirm={confirmAction}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
